import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import firebase from "firebase/app";
import "firebase/auth";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Grid,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Chip,
  Menu,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon, ArrowUpward as UpIcon, ArrowDownward as DownIcon, ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import { eventSubEventsRegistry, createEventAttribute } from "../../../../../data-store/actions/events-actions";
import { eventParticipants, eventCampaigns, registryAutorizedUsers } from "../../../../../data-store/actions/registry-actions";
import {
  listarModalidades,
  listarCategoriasDeModalidad,
  resolverModalidadLabel,
  resolverCategoriaLabel,
} from "../../../nextEvents/Registry/Configuration/BulkEdit/bulkEditLogic";
import { uid, BLOQUES_GENERICOS, bloqueGenericoPorDefecto, EditorBloqueGenerico } from "./genericBlockShared";
import EmailBlockPreview from "./EmailBlockPreview";

// Correo de CAMPAÑA a inscritos de un evento (guía del atleta, entrega de
// kits, etc.) — separado a propósito del correo de confirmación: usa una
// Cloud Function distinta (sendCampaignEmail, functions/modules/emailBlocks/
// sendCampaignEmail.js) que nunca toca enviarCorreoInscrito/
// plantillaConfirmacionInscripcion. La selección de destinatarios reusa la
// misma lógica de filtros que la edición masiva de inscritos
// (BulkEdit/bulkEditLogic.js), y el envío se manda en lotes desde el front
// con barra de progreso, mismo patrón que BulkEditInscritos.jsx.
const NOMBRE_RAMA = { V: "Varonil", F: "Femenil", M: "Mixto" };
// Tope real: Mailjet Send API v3.1 (la que usa sendMailFuncPromisse en el
// backend) rechaza la llamada COMPLETA si el array de Messages trae más de
// 50 — no es un límite inventado por este proyecto. Por eso debe ser <= el
// MAX_DESTINATARIOS_POR_LOTE del backend (sendCampaignEmail.js).
const LOTE_SIZE = 50;
const LOTE_DELAY_MS = 700;
const CAMPAIGN_URL =
  process.env.REACT_APP_SEND_CAMPAIGN_URL ||
  "https://us-central1-kmetasfirebase.cloudfunctions.net/sendCampaignEmail";
const ENVIO_CAMPANA_HABILITADO = true;

// Datos genéricos de ejemplo para rellenar las variables ([nombres] etc.)
// del correo cuando se manda un envío de prueba — no corresponde a ningún
// inscrito real, así que el backend no lo registra en el historial.
const PARTICIPANTE_PRUEBA_BASE = {
  nombres: "Juan",
  apellidos: "Pérez",
  numero: "0000",
};
const CORREO_VALIDO_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Solo estos dos bloques "de datos" de Dashport tienen sentido en un correo
// que no está atado a un registro específico — deben coincidir con
// TIPOS_PERMITIDOS de functions/modules/emailBlocks/renderCampaignEmail.js.
const ESTRUCTURA_CAMPANA = [
  { type: "organizerHeader", label: "Encabezado", descripcion: "Logo del organizador con el color de fondo del evento." },
  { type: "footer", label: "Pie de página", descripcion: "Nombre y link del organizador." },
];
const ESTRUCTURA_CAMPANA_TYPES = ESTRUCTURA_CAMPANA.map((b) => b.type);

function bloquesCampanaPorDefecto() {
  return [
    { id: uid(), type: "organizerHeader" },
    { id: uid(), type: "heading", text: "Título del correo", size: 22, align: "center" },
    {
      id: uid(),
      type: "text",
      text: "Hola [nombres], escribe aquí la información para los inscritos.",
      size: 14,
      color: "#111827",
      align: "left",
    },
    { id: uid(), type: "footer" },
  ];
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function CampaignEmailContainer() {
  const dispatch = useDispatch();
  const eventDetail = useSelector((state) => state.events.eventDetail);
  const eventParticipantsState = useSelector((state) => state.registry.eventParticipants);
  const eventSubEventsRegistryState = useSelector((state) => state.events.eventSubEventsRegistry);
  const registryConfiguration = useSelector((state) => state.registry.registryConfiguration);
  // Historial de campañas: vive en el árbol principal del evento (no en
  // inscripcionesConfiguracion, que se carga completa para cualquier
  // usuario autorizado) y se pide solo al entrar a esta vista — ver
  // eventCampaigns() en data-store/actions/registry-actions.js.
  const eventCampaignsState = useSelector((state) => state.registry.eventCampaigns);
  const registryAutorizedUsersState = useSelector((state) => state.registry.registryAutorizedUsers);
  const eventoIndex = "evento_" + eventDetail.iDEvento;

  useEffect(() => {
    dispatch(eventSubEventsRegistry(eventoIndex));
    dispatch(eventParticipants(eventoIndex));
    dispatch(eventCampaigns(eventoIndex));
    dispatch(registryAutorizedUsers(eventoIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoIndex]);

  const [abierto, setAbierto] = useState(false);
  const [mensajeGuardado, setMensajeGuardado] = useState("");
  const [correoAVer, setCorreoAVer] = useState(null);

  // Historial de envíos: el backend solo guarda el subject/blocks y el ID
  // de cada inscrito al que se le mandó (nunca su nombre/correo — evitamos
  // duplicar datos de contacto). Para mostrarlos aquí se cruzan esos IDs
  // contra la lista de inscritos que esta misma vista ya tiene cargada.
  const historial = useMemo(() => {
    const campañas = eventCampaignsState || {};
    return Object.entries(campañas)
      .map(([id, c]) => {
        const usuario = (registryAutorizedUsersState || {})[c.enviadoPor];
        return {
          id,
          subject: c.subject,
          fecha: c.fecha,
          tipo: c.tipo || "campana",
          blocks: c.blocks || [],
          enviadoPorNombre: usuario?.nombre || usuario?.correo || c.enviadoPor || "Desconocido",
          destinatarios: Object.keys(c.destinatarios || {}).map((inscritoKey) => {
            const p = (eventParticipantsState || {})[inscritoKey];
            return p
              ? { numero: p.numero, nombres: p.nombres, apellidos: p.apellidos, correo: p.correo }
              : { numero: "", nombres: "(inscrito eliminado)", apellidos: "", correo: inscritoKey };
          }),
        };
      })
      .sort((a, b) => (b.fecha || 0) - (a.fecha || 0));
  }, [eventCampaignsState, eventParticipantsState, registryAutorizedUsersState]);

  // ------------------ FILTROS Y SELECCIÓN DE DESTINATARIOS ------------------
  const [soloCompletos, setSoloCompletos] = useState(true);
  const [textoLibre, setTextoLibre] = useState("");
  const [modalidad, setModalidad] = useState("");
  const [categoriaValor, setCategoriaValor] = useState("");
  const [rama, setRama] = useState("");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const selectedSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

  const modalidadOptions = useMemo(
    () => listarModalidades(eventSubEventsRegistryState),
    [eventSubEventsRegistryState]
  );
  const categoriaOptions = useMemo(
    () => listarCategoriasDeModalidad(eventSubEventsRegistryState, modalidad),
    [eventSubEventsRegistryState, modalidad]
  );

  const enrichedRows = useMemo(() => {
    const list = Object.values(eventParticipantsState || {});
    return list.map((r) => ({
      ...r,
      __modalidadLabel: resolverModalidadLabel(eventSubEventsRegistryState, r.modalidad),
      __categoriaLabel: resolverCategoriaLabel(eventSubEventsRegistryState, r.modalidad, r.rama, r.categoria),
      __ramaLabel: NOMBRE_RAMA[r.rama] || r.rama || "",
    }));
  }, [eventParticipantsState, eventSubEventsRegistryState]);

  const filteredRows = useMemo(() => {
    const txt = textoLibre.trim().toLowerCase();
    return enrichedRows.filter((r) => {
      if (!r.correo) return false;
      if (soloCompletos && (!r.statusPago || r.statusPago.status !== "ok" || r.borrado)) return false;
      if (modalidad !== "" && String(r.modalidad) !== String(modalidad)) return false;
      if (rama !== "" && r.rama !== rama) return false;
      if (categoriaValor) {
        const [ramaSel, catSel] = categoriaValor.split("|");
        if (String(r.categoria) !== String(catSel) || r.rama !== ramaSel) return false;
      }
      if (txt) {
        const hay = `${r.numero} ${r.nombres} ${r.apellidos} ${r.correo}`.toLowerCase();
        if (!hay.includes(txt)) return false;
      }
      return true;
    });
  }, [enrichedRows, soloCompletos, modalidad, rama, categoriaValor, textoLibre]);

  const toggleSelected = useCallback((row) => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      if (s.has(row.key)) s.delete(row.key);
      else s.add(row.key);
      return Array.from(s);
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      filteredRows.forEach((r) => s.add(r.key));
      return Array.from(s);
    });
  }, [filteredRows]);

  const unselectAllVisible = useCallback(() => {
    setSelectedKeys((prev) => {
      const s = new Set(prev);
      filteredRows.forEach((r) => s.delete(r.key));
      return Array.from(s);
    });
  }, [filteredRows]);

  const headerAllChecked =
    filteredRows.length > 0 && filteredRows.every((r) => selectedSet.has(r.key));
  const headerIndeterminate =
    filteredRows.some((r) => selectedSet.has(r.key)) && !headerAllChecked;

  const destinatariosSeleccionados = useMemo(
    () => filteredRows.filter((r) => selectedSet.has(r.key)),
    [filteredRows, selectedSet]
  );

  // ------------------ TIPO DE CORREO ------------------
  // "campana": correo propio armado con bloques (lo de siempre).
  // "confirmacion": reenvía el correo de confirmación REAL (con los datos
  // de inscripción de cada quien) solo a los seleccionados arriba — mismo
  // mecanismo que usaba el botón viejo "Enviar correos" de Configuración de
  // inscripciones, ahora integrado aquí para poder elegir destinatarios en
  // vez de mandarlo siempre a todo el evento.
  const [tipoCorreo, setTipoCorreo] = useState("campana");

  // ------------------ COMPOSITOR DEL CORREO ------------------
  const [subject, setSubject] = useState("");
  const [blocks, setBlocks] = useState(bloquesCampanaPorDefecto);
  const [anchorAgregar, setAnchorAgregar] = useState(null);

  function cambiarTipoCorreo(nuevoTipo) {
    setTipoCorreo(nuevoTipo);
    if (nuevoTipo === "confirmacion" && subject.trim() === "") {
      setSubject("Reenvío de correo de confirmación");
    }
  }

  // ------------------ DISEÑOS GUARDADOS (plantillas) ------------------
  // Varios diseños independientes guardados a la vez (ej. "Guía del
  // atleta", "Entrega de kits") — cada uno se guarda/actualiza/borra por
  // separado, así que redactar uno nuevo nunca pisa los que ya existían.
  // Vive en inscripcionesConfiguracion/campaignDrafts/{id} (plural). El
  // "campaignDraft" (singular) viejo de antes de este cambio se muestra
  // como un diseño más, de solo lectura hasta que se guarde explícitamente
  // bajo un nombre — así ningún diseño previamente guardado se pierde.
  const [draftIdActual, setDraftIdActual] = useState(null);
  const [nombreDiseno, setNombreDiseno] = useState("");
  const [guardarComoAbierto, setGuardarComoAbierto] = useState(false);
  const [nombreNuevoDiseno, setNombreNuevoDiseno] = useState("");

  const disenosGuardados = useMemo(() => {
    const drafts = (registryConfiguration && registryConfiguration.campaignDrafts) || {};
    const lista = Object.keys(drafts).map((id) => ({ id, ...drafts[id] }));
    const legacy = registryConfiguration && registryConfiguration.campaignDraft;
    if (legacy && (legacy.subject || (legacy.blocks && legacy.blocks.length > 0))) {
      lista.push({
        id: "legacy",
        name: "Diseño guardado (antes de poder tener varios)",
        subject: legacy.subject,
        blocks: legacy.blocks,
        updatedAt: 0,
      });
    }
    return lista.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  }, [registryConfiguration]);

  function nuevoCorreoEnBlanco() {
    setBlocks(bloquesCampanaPorDefecto());
    setSubject("");
    setDraftIdActual(null);
    setNombreDiseno("");
  }

  function cargarDiseno(id) {
    if (id === "") {
      nuevoCorreoEnBlanco();
      return;
    }
    const d = disenosGuardados.find((x) => x.id === id);
    if (!d) return;
    setSubject(d.subject || "");
    setBlocks(
      (Array.isArray(d.blocks) && d.blocks.length > 0 ? d.blocks : bloquesCampanaPorDefecto()).map((b) => ({
        id: uid(),
        ...b,
      }))
    );
    setDraftIdActual(id);
    setNombreDiseno(d.name || "");
  }

  function abrirGuardarComo() {
    setNombreNuevoDiseno(nombreDiseno || "");
    setGuardarComoAbierto(true);
  }

  function confirmarGuardarComoNuevo() {
    const nombre = nombreNuevoDiseno.trim();
    if (!nombre) return;
    const nuevoId = uid();
    const payload = {
      name: nombre,
      subject,
      blocks: blocks.map(({ id, ...rest }) => rest),
      updatedAt: new Date().getTime(),
    };
    dispatch(createEventAttribute(eventoIndex, `inscripcionesConfiguracion/campaignDrafts/${nuevoId}`, payload)).finally(() => {
      setDraftIdActual(nuevoId);
      setNombreDiseno(nombre);
      setGuardarComoAbierto(false);
      setMensajeGuardado(`Diseño "${nombre}" guardado.`);
    });
  }

  function actualizarDisenoActual() {
    if (!draftIdActual || draftIdActual === "legacy") {
      abrirGuardarComo();
      return;
    }
    const payload = {
      name: nombreDiseno,
      subject,
      blocks: blocks.map(({ id, ...rest }) => rest),
      updatedAt: new Date().getTime(),
    };
    dispatch(
      createEventAttribute(eventoIndex, `inscripcionesConfiguracion/campaignDrafts/${draftIdActual}`, payload)
    ).finally(() => {
      setMensajeGuardado(`Diseño "${nombreDiseno}" actualizado.`);
    });
  }

  function eliminarDisenoActual() {
    if (!draftIdActual || draftIdActual === "legacy") return;
    if (!window.confirm(`¿Borrar el diseño "${nombreDiseno}"? Esta acción no se puede deshacer.`)) return;
    dispatch(createEventAttribute(eventoIndex, `inscripcionesConfiguracion/campaignDrafts/${draftIdActual}`, null)).finally(() => {
      nuevoCorreoEnBlanco();
      setMensajeGuardado("Diseño borrado.");
    });
  }

  const estructuraDisponible = useMemo(() => {
    const tiposUsados = new Set(blocks.map((b) => b.type));
    return ESTRUCTURA_CAMPANA.filter((b) => !tiposUsados.has(b.type));
  }, [blocks]);

  function moverBloque(idx, dir) {
    setBlocks((prev) => {
      const j = dir === "up" ? idx - 1 : idx + 1;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  function quitarBloque(idx) {
    setBlocks((prev) => prev.filter((_, i) => i !== idx));
  }

  function actualizarBloque(idx, patch) {
    setBlocks((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  }

  function agregarBloque(type) {
    const nuevo = ESTRUCTURA_CAMPANA_TYPES.includes(type) ? { id: uid(), type } : bloqueGenericoPorDefecto(type);
    setBlocks((prev) => [...prev, nuevo]);
    setAnchorAgregar(null);
  }

  // ------------------ ENVÍO ------------------
  const [confirmando, setConfirmando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const [logs, setLogs] = useState([]);
  const [resultado, setResultado] = useState(null);

  const log = (msg) => setLogs((prev) => [...prev, msg]);

  const puedeEnviar = subject.trim() !== "" && destinatariosSeleccionados.length > 0 && !enviando;

  // ------------------ ENVÍO DE PRUEBA ------------------
  // Manda el diseño actual a UN correo cualquiera, con datos de ejemplo
  // (PARTICIPANTE_PRUEBA_BASE) en vez de un inscrito real — para revisar
  // cómo se ve/llega antes de mandarlo de verdad a los inscritos
  // seleccionados. El backend no lo guarda en el historial de campañas.
  const [correoPrueba, setCorreoPrueba] = useState("");
  const [enviandoPrueba, setEnviandoPrueba] = useState(false);
  const [mensajePrueba, setMensajePrueba] = useState("");

  const puedeEnviarPrueba =
    tipoCorreo === "campana" &&
    subject.trim() !== "" &&
    CORREO_VALIDO_REGEX.test(correoPrueba.trim()) &&
    !enviandoPrueba &&
    !enviando;

  async function enviarPrueba() {
    if (!puedeEnviarPrueba) return;

    setEnviandoPrueba(true);
    setMensajePrueba("");

    const user = firebase.auth().currentUser;
    if (!user) {
      setMensajePrueba("No hay sesión activa, vuelve a iniciar sesión e intenta de nuevo.");
      setEnviandoPrueba(false);
      return;
    }
    const token = await user.getIdToken();

    try {
      const res = await fetch(CAMPAIGN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventoIndex,
          campaignId: uid(),
          tipo: "campana",
          subject,
          blocks: blocks.map(({ id, ...rest }) => rest),
          esPrueba: true,
          destinatarios: [{ id: "prueba", correo: correoPrueba.trim(), ...PARTICIPANTE_PRUEBA_BASE }],
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error enviando la prueba");
      setMensajePrueba(`Correo de prueba enviado a ${correoPrueba.trim()}.`);
    } catch (err) {
      setMensajePrueba(`Error enviando la prueba — ${err.message}`);
    }

    setEnviandoPrueba(false);
  }

  async function enviarCampana() {
    if (!ENVIO_CAMPANA_HABILITADO) return;

    setEnviando(true);
    setProgreso(0);
    setLogs([]);
    setResultado(null);

    const user = firebase.auth().currentUser;
    if (!user) {
      log("No hay sesión activa, vuelve a iniciar sesión e intenta de nuevo.");
      setEnviando(false);
      return;
    }
    const token = await user.getIdToken();

    const campaignId = uid();
    const payloadBlocks = tipoCorreo === "campana" ? blocks.map(({ id, ...rest }) => rest) : undefined;
    const lotes = [];
    for (let i = 0; i < destinatariosSeleccionados.length; i += LOTE_SIZE) {
      lotes.push(destinatariosSeleccionados.slice(i, i + LOTE_SIZE));
    }

    let totalEnviados = 0;
    let huboError = false;

    for (let i = 0; i < lotes.length; i++) {
      const destinatarios = lotes[i].map((r) => ({
        id: r.key,
        correo: r.correo,
        nombres: r.nombres,
        apellidos: r.apellidos,
        numero: r.numero,
      }));

      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(CAMPAIGN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ eventoIndex, campaignId, tipo: tipoCorreo, subject, blocks: payloadBlocks, destinatarios }),
        });
        // eslint-disable-next-line no-await-in-loop
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Error enviando el lote");
        totalEnviados += data.enviados;
        log(`Lote ${i + 1} de ${lotes.length}: ${data.enviados} correos enviados`);
      } catch (err) {
        huboError = true;
        log(`Lote ${i + 1} de ${lotes.length}: ERROR — ${err.message}`);
      }

      setProgreso(Math.round(((i + 1) / lotes.length) * 100));
      if (i + 1 < lotes.length) {
        // eslint-disable-next-line no-await-in-loop
        await delay(LOTE_DELAY_MS);
      }
    }

    setResultado({ totalEnviados, totalDestinatarios: destinatariosSeleccionados.length, huboError });
    setEnviando(false);
    setConfirmando(false);
  }

  function cerrarDialogo() {
    if (enviando) return;
    setAbierto(false);
    setConfirmando(false);
    setResultado(null);
    setLogs([]);
    setProgreso(0);
  }

  return (
    <Box mt={2}>
      <Button variant="contained" color="primary" onClick={() => setAbierto(true)}>
        Enviar correo a los inscritos
      </Button>

      <Dialog open={abierto} onClose={cerrarDialogo} maxWidth="lg" fullWidth>
        <DialogTitle>Enviar correo a inscritos de {eventDetail.nombreEvento}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 16 }}>
            Este correo es independiente del correo de confirmación de inscripción — úsalo para
            avisos como la guía del atleta, lugar y hora de entrega de kits, etc.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            1. Selecciona destinatarios
          </Typography>

          <Box mb={2} display="flex" gap={12} flexWrap="wrap" alignItems="center">
            <TextField
              label="Buscar (nombre, folio o correo)"
              value={textoLibre}
              onChange={(e) => setTextoLibre(e.target.value)}
              style={{ minWidth: 220 }}
              disabled={enviando}
            />
            <FormControl style={{ minWidth: 200 }}>
              <InputLabel>Modalidad</InputLabel>
              <Select
                value={modalidad}
                onChange={(e) => {
                  setModalidad(e.target.value);
                  setCategoriaValor("");
                }}
                disabled={enviando}
              >
                <MenuItem value="">Todas</MenuItem>
                {modalidadOptions.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ minWidth: 200 }} disabled={!modalidad || enviando}>
              <InputLabel>Categoría</InputLabel>
              <Select value={categoriaValor} onChange={(e) => setCategoriaValor(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {categoriaOptions.map((c) => (
                  <MenuItem key={c.rama + "|" + c.value} value={c.rama + "|" + c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl style={{ minWidth: 150 }}>
              <InputLabel>Género</InputLabel>
              <Select value={rama} onChange={(e) => setRama(e.target.value)} disabled={enviando}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="V">Varonil</MenuItem>
                <MenuItem value="F">Femenil</MenuItem>
                <MenuItem value="M">Mixto</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={soloCompletos}
                  onChange={(e) => setSoloCompletos(e.target.checked)}
                  disabled={enviando}
                />
              }
              label="Solo inscripciones completas"
            />
          </Box>

          <Box mb={1} display="flex" gap={12} alignItems="center">
            <Typography variant="body2">
              Filtrados: {filteredRows.length} | Seleccionados: {selectedKeys.length}
            </Typography>
            <Button size="small" onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())} disabled={enviando}>
              {headerAllChecked ? "Deseleccionar visibles" : "Seleccionar visibles"}
            </Button>
          </Box>

          <TableContainer component={Paper} style={{ maxHeight: 260 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={headerAllChecked}
                      indeterminate={headerIndeterminate}
                      onClick={() => (headerAllChecked ? unselectAllVisible() : selectAllVisible())}
                      disabled={enviando}
                    />
                  </TableCell>
                  <TableCell>Folio</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Correo</TableCell>
                  <TableCell>Modalidad</TableCell>
                  <TableCell>Categoría</TableCell>
                  <TableCell>Género</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  const selected = selectedSet.has(row.key);
                  return (
                    <TableRow key={row.key} hover style={{ cursor: "pointer" }} onClick={() => !enviando && toggleSelected(row)}>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected} disabled={enviando} />
                      </TableCell>
                      <TableCell>{row.numero}</TableCell>
                      <TableCell>{row.nombres} {row.apellidos}</TableCell>
                      <TableCell>{row.correo}</TableCell>
                      <TableCell>{row.__modalidadLabel}</TableCell>
                      <TableCell>{row.__categoriaLabel}</TableCell>
                      <TableCell>{row.__ramaLabel}</TableCell>
                    </TableRow>
                  );
                })}
                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2">No hay inscritos con los filtros actuales.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider style={{ margin: "20px 0" }} />

          <Typography variant="subtitle1" gutterBottom>
            2. Elige el tipo de correo
          </Typography>
          <RadioGroup
            value={tipoCorreo}
            onChange={(e) => cambiarTipoCorreo(e.target.value)}
            style={{ marginBottom: 8 }}
          >
            <FormControlLabel
              value="campana"
              control={<Radio disabled={enviando} />}
              label="Correo personalizado (guía del atleta, entrega de kits, etc.)"
            />
            <FormControlLabel
              value="confirmacion"
              control={<Radio disabled={enviando} />}
              label="Reenviar correo de confirmación de inscripción"
            />
          </RadioGroup>

          {tipoCorreo === "confirmacion" && (
            <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 16 }}>
              Se reenvía el correo de confirmación real de cada inscrito seleccionado, con sus
              propios datos (número, ficha de pago si aplica, etc.) — no se redacta contenido aquí.
            </Typography>
          )}

          {tipoCorreo === "campana" && (
            <>
              <Typography variant="subtitle1" gutterBottom>
                3. Redacta el correo
              </Typography>

              <Box mb={2} display="flex" gap={12} flexWrap="wrap" alignItems="center">
                <FormControl style={{ minWidth: 240 }}>
                  <InputLabel>Diseño guardado</InputLabel>
                  <Select value={draftIdActual || ""} onChange={(e) => cargarDiseno(e.target.value)} disabled={enviando}>
                    <MenuItem value="">(Correo nuevo en blanco)</MenuItem>
                    {disenosGuardados.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name || "(sin nombre)"}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" size="small" onClick={abrirGuardarComo} disabled={enviando}>
                  Guardar como nuevo diseño
                </Button>
                {draftIdActual && draftIdActual !== "legacy" && (
                  <>
                    <Button variant="outlined" size="small" onClick={actualizarDisenoActual} disabled={enviando}>
                      Actualizar "{nombreDiseno}"
                    </Button>
                    <Button variant="outlined" size="small" color="secondary" onClick={eliminarDisenoActual} disabled={enviando}>
                      Borrar este diseño
                    </Button>
                  </>
                )}
                {mensajeGuardado && (
                  <Typography variant="body2" style={{ opacity: 0.75 }}>
                    {mensajeGuardado}
                  </Typography>
                )}
              </Box>

              <TextField
                label="Asunto"
                fullWidth
                margin="dense"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={enviando}
                style={{ marginBottom: 16 }}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {blocks.map((b, idx) => {
                const esDashport = ESTRUCTURA_CAMPANA_TYPES.includes(b.type);
                const infoDashport = esDashport && ESTRUCTURA_CAMPANA.find((d) => d.type === b.type);
                const infoGenerico = !esDashport && BLOQUES_GENERICOS.find((d) => d.type === b.type);
                return (
                  <Paper key={b.id} variant="outlined" style={{ padding: 12, marginBottom: 10 }}>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Chip
                        size="small"
                        label={infoDashport ? infoDashport.label : infoGenerico?.label || b.type}
                        color={esDashport ? "primary" : "default"}
                      />
                      <Box flexGrow={1} />
                      <IconButton size="small" onClick={() => moverBloque(idx, "up")} disabled={idx === 0 || enviando}>
                        <UpIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => moverBloque(idx, "down")} disabled={idx === blocks.length - 1 || enviando}>
                        <DownIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => quitarBloque(idx)} disabled={enviando}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {esDashport && (
                      <Typography variant="caption" style={{ display: "block", marginTop: 6, opacity: 0.7 }}>
                        {infoDashport.descripcion}
                      </Typography>
                    )}
                    {!esDashport && (
                      <Box mt={1}>
                        <EditorBloqueGenerico block={b} eventoIndex={eventoIndex} onChange={(patch) => actualizarBloque(idx, patch)} />
                      </Box>
                    )}
                  </Paper>
                );
              })}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={(e) => setAnchorAgregar(e.currentTarget)}
                disabled={enviando}
              >
                Agregar bloque
              </Button>
              <Menu anchorEl={anchorAgregar} open={!!anchorAgregar} onClose={() => setAnchorAgregar(null)}>
                {estructuraDisponible.length > 0 && (
                  <Typography variant="caption" style={{ padding: "6px 16px", display: "block", opacity: 0.6 }}>
                    Estructura
                  </Typography>
                )}
                {estructuraDisponible.map((b) => (
                  <MenuItem key={b.type} onClick={() => agregarBloque(b.type)}>
                    {b.label}
                  </MenuItem>
                ))}
                <Divider />
                <Typography variant="caption" style={{ padding: "6px 16px", display: "block", opacity: 0.6 }}>
                  Bloque personalizado
                </Typography>
                {BLOQUES_GENERICOS.map((b) => (
                  <MenuItem key={b.type} onClick={() => agregarBloque(b.type)}>
                    {b.label}
                  </MenuItem>
                ))}
              </Menu>

            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>
                Vista previa (aproximada, con un atleta de ejemplo)
              </Typography>
              <EmailBlockPreview blocks={blocks} eventDetail={eventDetail} />

              <Box mt={2} p={2} component={Paper} variant="outlined">
                <Typography variant="subtitle2" gutterBottom>
                  Enviar correo de prueba
                </Typography>
                <Typography variant="body2" style={{ opacity: 0.75, marginBottom: 8 }}>
                  Manda este diseño a un correo cualquiera (con datos de ejemplo en vez de un
                  inscrito real) antes de mandarlo de verdad. No queda en el historial.
                </Typography>
                <Box display="flex" gap={8} flexWrap="wrap" alignItems="center">
                  <TextField
                    label="Correo de prueba"
                    value={correoPrueba}
                    onChange={(e) => setCorreoPrueba(e.target.value)}
                    disabled={enviandoPrueba || enviando}
                    style={{ minWidth: 240 }}
                  />
                  <Button
                    variant="outlined"
                    onClick={enviarPrueba}
                    disabled={!puedeEnviarPrueba}
                  >
                    {enviandoPrueba ? "Enviando..." : "Enviar prueba"}
                  </Button>
                </Box>
                {mensajePrueba && (
                  <Typography variant="body2" style={{ marginTop: 8, opacity: 0.85 }}>
                    {mensajePrueba}
                  </Typography>
                )}
              </Box>
            </Grid>
              </Grid>
            </>
          )}

          {enviando && (
            <Box mt={2}>
              <LinearProgress variant="determinate" value={progreso} />
              <Typography variant="body2" style={{ marginTop: 8 }}>
                {progreso}% completado
              </Typography>
              <Box
                mt={1}
                style={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  padding: 10,
                  maxHeight: 160,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 13,
                }}
              >
                {logs.map((l, i) => (
                  <div key={i}>{l}</div>
                ))}
              </Box>
            </Box>
          )}

          {resultado && !enviando && (
            <Typography
              variant="body2"
              style={{ marginTop: 16, fontWeight: "bold" }}
              color={resultado.huboError ? "error" : "primary"}
            >
              {resultado.totalEnviados} de {resultado.totalDestinatarios} correos enviados
              {resultado.huboError ? " — hubo errores en algún lote, revisa el detalle arriba." : "."}
            </Typography>
          )}

          {confirmando && !enviando && !resultado && (
            <Typography variant="body2" color="error" style={{ marginTop: 16, fontWeight: "bold" }}>
              {tipoCorreo === "confirmacion"
                ? `¿Confirmas reenviar el correo de confirmación a ${destinatariosSeleccionados.length} inscritos?`
                : `¿Confirmas mandar este correo a ${destinatariosSeleccionados.length} inscritos?`}{" "}
              Esta acción no se puede deshacer.
            </Typography>
          )}

          {!ENVIO_CAMPANA_HABILITADO && (
            <Typography variant="body2" style={{ marginTop: 16, opacity: 0.75 }}>
              El envío todavía está deshabilitado mientras se termina de probar — por ahora puedes
              seleccionar destinatarios, redactar y guardar el diseño.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogo} disabled={enviando}>
            Cerrar
          </Button>
          {!confirmando && !resultado && (
            <Button
              variant="contained"
              color="primary"
              disabled={!puedeEnviar || !ENVIO_CAMPANA_HABILITADO}
              onClick={() => setConfirmando(true)}
            >
              {tipoCorreo === "confirmacion" ? "Reenviar confirmación a " : "Enviar a "}
              {destinatariosSeleccionados.length} inscritos
            </Button>
          )}
          {confirmando && !resultado && (
            <Button variant="contained" color="secondary" disabled={enviando} onClick={enviarCampana}>
              Sí, {tipoCorreo === "confirmacion" ? "reenviar a " : "enviar a "}
              {destinatariosSeleccionados.length} inscritos
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {historial.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            Historial de correos enviados
          </Typography>
          {historial.map((c) => (
            <Accordion key={c.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                  <Typography>{c.subject}</Typography>
                  {c.tipo === "confirmacion" && (
                    <Chip size="small" label="Confirmación" color="primary" />
                  )}
                </Box>
                <Typography variant="body2" style={{ opacity: 0.7, marginRight: 12 }}>
                  {c.fecha ? new Date(c.fecha).toLocaleString() : ""} — enviado por {c.enviadoPorNombre} —{" "}
                  {c.destinatarios.length} destinatarios
                </Typography>
              </AccordionSummary>
              <AccordionDetails style={{ display: "block" }}>
                <Box mb={1}>
                  {c.tipo === "confirmacion" ? (
                    <Typography variant="caption" style={{ opacity: 0.7, fontStyle: "italic" }}>
                      Correo de confirmación real de cada inscrito — el contenido varía por persona,
                      no se puede previsualizar aquí.
                    </Typography>
                  ) : (
                    <Button size="small" variant="outlined" onClick={() => setCorreoAVer(c)}>
                      Ver correo enviado
                    </Button>
                  )}
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Folio</TableCell>
                      <TableCell>Nombre</TableCell>
                      <TableCell>Correo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {c.destinatarios.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell>{d.numero}</TableCell>
                        <TableCell>{d.nombres} {d.apellidos}</TableCell>
                        <TableCell>{d.correo}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <Dialog open={!!correoAVer} onClose={() => setCorreoAVer(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{correoAVer?.subject}</DialogTitle>
        <DialogContent dividers>
          {correoAVer && (
            <EmailBlockPreview
              blocks={correoAVer.blocks.map((b, i) => ({ id: i, ...b }))}
              eventDetail={eventDetail}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCorreoAVer(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={guardarComoAbierto} onClose={() => setGuardarComoAbierto(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Guardar como nuevo diseño</DialogTitle>
        <DialogContent>
          <TextField
            label="Nombre del diseño"
            fullWidth
            autoFocus
            margin="dense"
            value={nombreNuevoDiseno}
            onChange={(e) => setNombreNuevoDiseno(e.target.value)}
            placeholder="Ej. Guía del atleta"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGuardarComoAbierto(false)}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={confirmarGuardarComoNuevo} disabled={!nombreNuevoDiseno.trim()}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
